import { envelopeData } from '@/lib/envelope'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { resolveEntityQueryOptions } from '@/lib/queries/lesson'
import { courseDetailQueryOptions } from '@/lib/queries/catalog'
import { CourseLayoutProvider } from '@/components/watch/course-layout-context'
import { CourseSidebarPanel } from '@/components/watch/course-sidebar-panel'
import { formatDuration } from '@/lib/utils'

export const Route = createFileRoute('/_app/$menu/$programa')({
  loader: async ({ context, params }) => {
    let entity
    try {
      entity = envelopeData(
        await context.queryClient.fetchQuery(
          resolveEntityQueryOptions(params.menu, params.programa),
        ),
      ) ?? null
    } catch {
      return { entity: null }
    }
    if (entity?.entityId) {
      await context.queryClient.prefetchQuery(courseDetailQueryOptions(entity.entityId))
    }
    return { entity }
  },
  component: ProgramLayout,
  errorComponent: () => (
    <div className="text-center py-16">
      <p className="text-muted-foreground">Erro ao carregar programa. Tente recarregar a página.</p>
    </div>
  ),
})

function ProgramLayout() {
  const { menu, programa: program } = Route.useParams()

  const { data: resolveData } = useSuspenseQuery(resolveEntityQueryOptions(menu, program))
  const entity = envelopeData(resolveData)

  const courseId = entity?.entityId
  const { data: courseData } = useSuspenseQuery(courseDetailQueryOptions(courseId ?? ''))
  const course = envelopeData(courseData)

  const hasAccess = entity?.hasAccess !== false && course?.hasAccess !== false
  const isLocked = !hasAccess

  // `entity` entra na condição (D1b): o `any` escondia que a árvore lê
  // `entity.menuSlug` — sem ela, o ramo quebrava em vez de cair em `null`.
  const sidebarTree = course && entity && (course.modules?.length ?? 0) > 0 ? {
    title: course.name,
    programHref: `/${entity.menuSlug}/${entity.programSlug}`,
    modules: (course.modules ?? []).map((mod: any) => ({
      id: mod.id,
      title: mod.name,
      lessons: (mod.lessons || []).map((l: any) => ({
        id: l.id,
        title: l.title,
        slug: l.slug,
        menuSlug: entity.menuSlug,
        programSlug: entity.programSlug,
        duration: formatDuration(l.estimatedDuration),
      })),
    })),
  } : null

  return (
    <CourseLayoutProvider
      courseTree={sidebarTree}
      completedLessonIds={course?.completedLessonIds ?? []}
      isLocked={isLocked}
    >
      <div className="flex gap-4 max-w-[1400px] mx-auto">
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
        <CourseSidebarPanel />
      </div>
    </CourseLayoutProvider>
  )
}
